import React, { useState } from 'react';
import {
  Box,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Typography,
  Stack,
  Chip,
  Button,
  Collapse,
  Badge,
  Grid,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Visibility,
  Search,
  KeyboardArrowDown,
  KeyboardArrowUp,
  People,
  ContentCopy,
  AddCircle,
  WarningAmber,
  Build,
} from '@mui/icons-material';
import {
  PaymentLink,
  PaymentStatus,
  Payment,
  ProcessingReconciliationDetails,
} from '../../types/PaymentLink';
import { formatCurrency, formatDateTime } from '../../utils/validators';
import { getCurrentUser, isSuperAdmin } from '../../services/authService';
import {
  getProcessingReconciliationDetails,
  reconcileProcessingPayment,
  softDeleteManualPayment,
} from '../../services/paymentLinkService';
import StatusChip from '../common/StatusChip';
import CopyLinkButton from '../common/CopyLinkButton';
import PaymentHistorySubRow from './PaymentHistorySubRow';
import AddManualPaymentDialog from './AddManualPaymentDialog';
import { toast } from 'react-toastify';

interface PaymentLinksTableProps {
  paymentLinks: PaymentLink[];
  onViewDetails: (paymentLink: PaymentLink) => void;
  onRefresh: () => void;
}

const PaymentLinksTable: React.FC<PaymentLinksTableProps> = ({
  paymentLinks,
  onViewDetails,
  onRefresh,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Check if current user is superuser
  const currentUser = getCurrentUser();
  const isSuperUser = isSuperAdmin(currentUser);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50); // Updated to 50
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'all'>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showOldCompleted, setShowOldCompleted] = useState(false);
  const [showStuckOnly, setShowStuckOnly] = useState(false);
  const [reconcilingId, setReconcilingId] = useState<string | null>(null);
  const [reconcileModalOpen, setReconcileModalOpen] = useState(false);
  const [selectedLinkForReconcile, setSelectedLinkForReconcile] = useState<PaymentLink | null>(null);
  const [bankVerified, setBankVerified] = useState(false);
  const [reconcileBankTransactionId, setReconcileBankTransactionId] = useState('');
  const [reconciliationDetails, setReconciliationDetails] =
    useState<ProcessingReconciliationDetails | null>(null);
  const [reconciliationDetailsLoading, setReconciliationDetailsLoading] = useState(false);
  const [reconciliationDetailsError, setReconciliationDetailsError] = useState<string | null>(null);
  // Manual payment dialog state
  const [manualPaymentDialogOpen, setManualPaymentDialogOpen] = useState(false);
  const [manualPaymentDialogMode, setManualPaymentDialogMode] = useState<'add' | 'edit'>('add');
  const [selectedLinkForPayment, setSelectedLinkForPayment] = useState<PaymentLink | null>(null);
  const [selectedManualPayment, setSelectedManualPayment] = useState<Payment | null>(null);
  const [manualActionLoadingPaymentId, setManualActionLoadingPaymentId] = useState<string | null>(null);
  const [deleteManualConfirmOpen, setDeleteManualConfirmOpen] = useState(false);
  const [pendingManualDelete, setPendingManualDelete] = useState<{
    link: PaymentLink;
    payment: Payment;
  } | null>(null);

  const isStuckProcessing = (link: PaymentLink): boolean => {
    if (link.status !== 'processing') return false;
    const updatedAt = link.updatedAt?.toDate?.() || link.createdAt?.toDate?.();
    if (!updatedAt) return false;
    return Date.now() - updatedAt.getTime() >= 10 * 60 * 1000;
  };

  const getCallbackStatusLabel = (link: PaymentLink): string => {
    return (
      link.callbackTracking?.lastStatus ||
      (link.callbackTracking?.received ? 'received' : '') ||
      (link.ziraatErrorMessage ? `error: ${link.ziraatErrorMessage}` : '') ||
      'unknown'
    );
  };

  const getReconcileBlockReason = (link: PaymentLink): string | null => {
    if (link.status === 'failed') {
      return 'Payment status is failed';
    }

    const statusText = `${getCallbackStatusLabel(link)} ${link.ziraatErrorMessage || ''}`.toLowerCase();
    const blockedKeywords = [
      'error',
      'failed',
      '3d_failed',
      'txn_failed',
      'limit yetersiz',
      'genel red',
      'declined',
      'reject',
      'red',
    ];

    const matched = blockedKeywords.find((keyword) => statusText.includes(keyword));
    if (matched) {
      return `Detected decline/failure state (${matched})`;
    }

    return null;
  };

  const shouldShowWarningIcon = (link: PaymentLink): boolean => {
    if (link.remainingAmount <= 0) return false;
    return isStuckProcessing(link);
  };

  const shouldShowReconcileIcon = (link: PaymentLink): boolean => {
    if (link.remainingAmount <= 0) return false;
    return !!getReconcileBlockReason(link);
  };

  const handleRowToggle = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Separate parent and child links
  // Filter deleted links unless user is superuser
  const parentLinks = paymentLinks.filter(link => {
    // If not a child link
    if (link.splitParentId) return false;
    // Show deleted only to superuser
    if (link.deleted && !isSuperUser) return false;
    return true;
  });
  const childLinksByParent = paymentLinks
    .filter(link => link.splitParentId)
    .reduce((acc, link) => {
      const parentId = link.splitParentId!;
      if (!acc[parentId]) acc[parentId] = [];
      acc[parentId].push(link);
      return acc;
    }, {} as Record<string, PaymentLink[]>);

  // Filter and search (only on parent links)
  const filteredLinks = parentLinks.filter((link) => {
    const matchesSearch =
      link.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      link.linkId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      link.customerEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (link.invoiceNumber && link.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || link.status === statusFilter;

    // Filter out old completed payments unless showOldCompleted is true
    let isRecent = true;
    if (!showOldCompleted && link.invoicePaid) {
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      
      // Check if link was completed more than 3 days ago
      const updatedAt = link.updatedAt?.toDate ? link.updatedAt.toDate() : link.updatedAt as any;
      const updatedDate = updatedAt instanceof Date ? updatedAt : new Date();
      isRecent = updatedDate > threeDaysAgo;
    }

    const matchesStuck = !showStuckOnly || isStuckProcessing(link) || !!getReconcileBlockReason(link);

    return matchesSearch && matchesStatus && isRecent && matchesStuck;
  });

  // Pagination (only parent links)
  const paginatedLinks = filteredLinks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Manual payment handlers
  const handleOpenManualPayment = (link: PaymentLink) => {
    setManualPaymentDialogMode('add');
    setSelectedManualPayment(null);
    setSelectedLinkForPayment(link);
    setManualPaymentDialogOpen(true);
  };

  const handleOpenEditManualPayment = (link: PaymentLink, payment: Payment) => {
    setManualPaymentDialogMode('edit');
    setSelectedLinkForPayment(link);
    setSelectedManualPayment(payment);
    setManualPaymentDialogOpen(true);
  };

  const getVisiblePayments = (link: PaymentLink): Payment[] => {
    return (link.payments || []).filter((payment) => payment.status === 'success' && !payment.isDeleted);
  };

  const handleCloseManualPayment = () => {
    setManualPaymentDialogOpen(false);
    setManualPaymentDialogMode('add');
    setSelectedLinkForPayment(null);
    setSelectedManualPayment(null);
  };

  const handleManualPaymentSuccess = () => {
    onRefresh();
  };

  const handleRequestSoftDeleteManualPayment = (link: PaymentLink, payment: Payment) => {
    if (!payment.id) {
      toast.error('Manual payment ID is missing');
      return;
    }
    setPendingManualDelete({ link, payment });
    setDeleteManualConfirmOpen(true);
  };

  const handleCloseManualDeleteConfirm = () => {
    if (manualActionLoadingPaymentId) return;
    setDeleteManualConfirmOpen(false);
    setPendingManualDelete(null);
  };

  const handleConfirmSoftDeleteManualPayment = async () => {
    if (!pendingManualDelete || !pendingManualDelete.payment.id) {
      return;
    }

    const { link, payment } = pendingManualDelete;

    setDeleteManualConfirmOpen(false);

    try {
      setManualActionLoadingPaymentId(payment.id);
      await softDeleteManualPayment({
        paymentLinkId: link.id,
        paymentId: payment.id,
      });
      toast.success('Manual payment deleted');
      onRefresh();
      setPendingManualDelete(null);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete manual payment');
      setDeleteManualConfirmOpen(true);
    } finally {
      setManualActionLoadingPaymentId(null);
    }
  };

  const handleOpenReconcileModal = (link: PaymentLink) => {
    setSelectedLinkForReconcile(link);
    setBankVerified(false);
    setReconcileBankTransactionId('');
    setReconciliationDetails(null);
    setReconciliationDetailsError(null);
    setReconciliationDetailsLoading(true);
    setReconcileModalOpen(true);

    void getProcessingReconciliationDetails(link.id)
      .then((details) => {
        setReconciliationDetails(details);
      })
      .catch((error: any) => {
        setReconciliationDetailsError(
          error?.message || 'Could not load the bank payment attempt details'
        );
      })
      .finally(() => {
        setReconciliationDetailsLoading(false);
      });
  };

  const handleCloseReconcileModal = () => {
    if (reconcilingId) return;
    setReconcileModalOpen(false);
    setSelectedLinkForReconcile(null);
    setBankVerified(false);
    setReconcileBankTransactionId('');
    setReconciliationDetails(null);
    setReconciliationDetailsError(null);
    setReconciliationDetailsLoading(false);
  };

  const getEffectiveReconcileBlockReason = (): string | null => {
    if (!selectedLinkForReconcile) return null;
    return (
      getReconcileBlockReason(selectedLinkForReconcile) ||
      reconciliationDetails?.blockReason ||
      reconciliationDetailsError
    );
  };

  const handleQuickReconcile = async () => {
    if (
      !selectedLinkForReconcile ||
      !reconciliationDetails?.canReconcile ||
      reconciliationDetails.attemptedAmount === null
    ) {
      return;
    }

    try {
      setReconcilingId(selectedLinkForReconcile.id);
      await reconcileProcessingPayment({
        paymentLinkId: selectedLinkForReconcile.id,
        paymentDate: new Date(),
        ...(reconcileBankTransactionId.trim() && {
          bankTransactionId: reconcileBankTransactionId.trim(),
        }),
        note: 'Quick reconcile from stuck-processing table action',
      });
      toast.success(
        `${formatCurrency(
          reconciliationDetails.attemptedAmount,
          reconciliationDetails.currency || selectedLinkForReconcile.currency
        )} payment reconciled successfully.`
      );
      handleCloseReconcileModal();
      onRefresh();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to reconcile processing payment');
    } finally {
      setReconcilingId(null);
    }
  };

  // Mobile card view
  if (isMobile) {
    return (
      <Box>
        {/* Search and Filter */}
        <Stack spacing={2} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by name, email, link ID, or invoice number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              select
              fullWidth
              label="Filter by Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as PaymentStatus | 'all')}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="visited">Visited</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </TextField>
            <Tooltip title={showOldCompleted ? "Hide old completed" : "Show old completed (3+ days)"}>
              <Button
                variant={showOldCompleted ? "contained" : "outlined"}
                onClick={() => setShowOldCompleted(!showOldCompleted)}
                sx={{ minWidth: 'fit-content', whiteSpace: 'nowrap' }}
              >
                {showOldCompleted ? "Hide Old" : "Show Old"}
              </Button>
            </Tooltip>
            <Tooltip title={showStuckOnly ? 'Show all statuses' : 'Show only stuck processing links (10+ min)'}>
              <Button
                variant={showStuckOnly ? 'contained' : 'outlined'}
                color={showStuckOnly ? 'warning' : 'inherit'}
                onClick={() => setShowStuckOnly(!showStuckOnly)}
                sx={{ minWidth: 'fit-content', whiteSpace: 'nowrap' }}
              >
                {showStuckOnly ? 'Stuck Only' : 'Stuck Filter'}
              </Button>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Mobile Cards */}
        <Stack spacing={2}>
          {paginatedLinks.map((link) => {
            const children = childLinksByParent[link.id] || [];
            const hasChildren = link.isSplitPayment && children.length > 0;

            return (
            <Card
              key={link.id}
              elevation={2}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                bgcolor: link.deleted ? '#ffebee' : 'inherit',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                  bgcolor: link.deleted ? '#ffcdd2' : undefined,
                },
              }}
              onClick={() => onViewDetails(link)}
            >
              <Stack spacing={2} sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Chip
                      label={link.linkId}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', mb: 0.5 }}
                    />
                    {hasChildren && (
                      <Chip
                        label={`Split Payment (${children.length} payers)`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        icon={<People fontSize="small" />}
                        sx={{ ml: 0.5 }}
                      />
                    )}
                    <Typography variant="body1" fontWeight={600}>
                      {link.customerName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {link.customerEmail}
                    </Typography>
                    {link.invoiceNumber && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Invoice: {link.invoiceNumber}
                      </Typography>
                    )}
                  </Box>
                  {(() => {
                    // Determine status based on payment state
                    let displayStatus: PaymentStatus = link.status;
                    if (link.totalPaid === 0) {
                      displayStatus = 'pending';
                    } else if (link.remainingAmount > 0) {
                      displayStatus = 'partial';
                    } else if (link.remainingAmount === 0) {
                      displayStatus = 'success';
                    }
                    return <StatusChip status={displayStatus} size="small" />;
                  })()}
                </Box>

                <Box>
                  <Typography variant="h5" color="primary" sx={{ mb: 1 }}>
                    {formatCurrency(link.totalPaid, link.currency)} / {formatCurrency(link.invoiceAmount, link.currency)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {link.remainingAmount > 0
                      ? `Remaining: ${formatCurrency(link.remainingAmount, link.currency)}`
                      : 'Fully Paid'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last Update: {formatDateTime(
                      (link.lastPaymentUpdate || link.createdAt).toDate()
                    )}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                  {link.type !== 'guest' && !hasChildren && (
                    <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                      <CopyLinkButton linkUrl={link.linkUrl} variant="icon" size="small" />
                    </Box>
                  )}
                  <Tooltip
                    title="View Details"
                    placement="top"
                    arrow
                    disableInteractive
                    PopperProps={{
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -8]
                          }
                        }
                      ]
                    }}
                    TransitionProps={{
                      timeout: 0
                    }}
                  >
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onViewDetails(link); }} color="info">
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  {/* Manual Payment Button - only show if there's remaining balance */}
                  {link.remainingAmount > 0 && (
                    <Tooltip
                      title="Add Manual Payment"
                      placement="top"
                      arrow
                      disableInteractive
                      PopperProps={{
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -8]
                            }
                          }
                        ]
                      }}
                      TransitionProps={{
                        timeout: 0
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleOpenManualPayment(link); }}
                        color="success"
                      >
                        <AddCircle />
                      </IconButton>
                    </Tooltip>
                  )}
                  {shouldShowWarningIcon(link) && (
                    <Tooltip title={`Processing timeout: ${getCallbackStatusLabel(link)}`}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleOpenReconcileModal(link); }}
                        sx={{ color: '#ff9800' }}
                      >
                        <WarningAmber />
                      </IconButton>
                    </Tooltip>
                  )}
                  {shouldShowReconcileIcon(link) && (
                    <Tooltip title="Transaction declined/failed - reconcile required">
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={(e) => { e.stopPropagation(); handleOpenReconcileModal(link); }}
                      >
                        <Build />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            </Card>
          );})}
        </Stack>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredLinks.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Box>
    );
  }

  // Desktop table view
  return (
    <Box>
      {/* Search and Filter */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by name, email, link ID, or invoice number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        <TextField
          select
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as PaymentStatus | 'all')}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="visited">Visited</MenuItem>
          <MenuItem value="processing">Processing</MenuItem>
          <MenuItem value="success">Success</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="expired">Expired</MenuItem>
        </TextField>
        <Tooltip title={showOldCompleted ? "Hide old completed payments (3+ days)" : "Show old completed payments (3+ days)"}>
          <Button
            variant={showOldCompleted ? "contained" : "outlined"}
            onClick={() => setShowOldCompleted(!showOldCompleted)}
            sx={{ minWidth: 140 }}
          >
            {showOldCompleted ? "Hide Old" : "Show Old"}
          </Button>
        </Tooltip>
        <Tooltip title={showStuckOnly ? 'Show all statuses' : 'Show only stuck processing links (10+ min)'}>
          <Button
            variant={showStuckOnly ? 'contained' : 'outlined'}
            color={showStuckOnly ? 'warning' : 'inherit'}
            onClick={() => setShowStuckOnly(!showStuckOnly)}
            sx={{ minWidth: 140 }}
          >
            {showStuckOnly ? 'Stuck Only' : 'Stuck Filter'}
          </Button>
        </Tooltip>
      </Stack>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px" />
                <TableCell>Link ID</TableCell>
                <TableCell>Invoice #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Update</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedLinks.map((link) => {
                const children = childLinksByParent[link.id] || [];
                const hasChildren = link.isSplitPayment && children.length > 0;
                const paymentCount = getVisiblePayments(link).length;
                const hasPayments = paymentCount > 0;
                const isExpanded = expandedRows.has(link.id);
                const hasExpandableContent = hasChildren || hasPayments;

                return (
                  <React.Fragment key={link.id}>
                    {/* Main Row */}
                    <TableRow
                      hover
                      sx={{
                        bgcolor: link.deleted ? '#ffebee' : hasChildren ? 'action.hover' : 'inherit',
                        cursor: hasExpandableContent ? 'pointer' : 'default',
                        '&:hover': {
                          bgcolor: link.deleted ? '#ffcdd2' : undefined,
                        },
                      }}
                      onClick={() => hasExpandableContent && handleRowToggle(link.id)}
                    >
                      {/* Expand/Collapse Icon */}
                      <TableCell>
                        <Tooltip
                          title={
                            hasChildren
                              ? (isExpanded ? 'Hide breakdown' : `Show ${children.length} payers`)
                              : hasPayments
                                ? (isExpanded ? 'Hide payments' : `Show ${paymentCount} payment${paymentCount !== 1 ? 's' : ''}`)
                                : 'No content'
                          }
                          placement="top"
                          arrow
                          disableInteractive
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={!hasExpandableContent}
                              sx={{ color: hasExpandableContent ? 'primary.main' : 'action.disabled' }}
                            >
                              <Badge
                                badgeContent={hasChildren ? children.length : paymentCount}
                                color={hasChildren ? 'primary' : 'success'}
                                invisible={!hasExpandableContent}
                                sx={{
                                  '& .MuiBadge-badge': {
                                    fontSize: '0.65rem',
                                    height: '16px',
                                    minWidth: '16px',
                                    padding: '0 4px',
                                  }
                                }}
                              >
                                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </Badge>
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            label={link.linkId}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace' }}
                          />
                          {hasChildren && (
                            <Chip
                              label="Split Payment"
                              size="small"
                              color="primary"
                              variant="outlined"
                              icon={<People fontSize="small" />}
                            />
                          )}
                          {link.type !== 'guest' && !hasChildren && (
                            <CopyLinkButton linkUrl={link.linkUrl} variant="icon" size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {link.invoiceNumber || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {link.customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {link.customerEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight={600} color="primary">
                            {formatCurrency(link.totalPaid, link.currency)} / {formatCurrency(link.invoiceAmount, link.currency)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {link.remainingAmount > 0
                              ? `Remaining: ${formatCurrency(link.remainingAmount, link.currency)}`
                              : 'Fully Paid'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Determine status based on payment state
                          let displayStatus: PaymentStatus = link.status;

                          if (link.totalPaid === 0) {
                            displayStatus = 'pending';
                          } else if (link.remainingAmount > 0) {
                            displayStatus = 'partial';
                          } else if (link.remainingAmount === 0) {
                            displayStatus = 'success';
                          }

                          return <StatusChip status={displayStatus} size="small" />;
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Use lastPaymentUpdate (payments/errors only) or fallback to createdAt
                          const lastUpdate = (link.lastPaymentUpdate || link.createdAt).toDate();
                          const now = new Date();
                          const diffMs = now.getTime() - lastUpdate.getTime();
                          const diffDays = diffMs / (1000 * 60 * 60 * 24);
                          let textColor = 'text.primary';
                          if (diffDays < 1) {
                            textColor = 'success.main'; // Recent (< 24h): Green
                          } else if (diffDays > 7) {
                            textColor = 'text.secondary'; // Old (> 7 days): Gray
                          }
                          return (
                            <Typography variant="body2" sx={{ color: textColor }}>
                              {formatDateTime(lastUpdate)}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip
                            title="View Details"
                            placement="top"
                            arrow
                            disableInteractive
                            PopperProps={{
                              modifiers: [
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [0, -8]
                                  }
                                }
                              ]
                            }}
                            TransitionProps={{
                              timeout: 0
                            }}
                          >
                            <IconButton size="small" onClick={() => onViewDetails(link)} color="info">
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          {/* Manual Payment Button - only show if there's remaining balance */}
                          {link.remainingAmount > 0 && (
                            <Tooltip
                              title="Add Manual Payment"
                              placement="top"
                              arrow
                              disableInteractive
                              PopperProps={{
                                modifiers: [
                                  {
                                    name: 'offset',
                                    options: {
                                      offset: [0, -8]
                                    }
                                  }
                                ]
                              }}
                              TransitionProps={{
                                timeout: 0
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleOpenManualPayment(link)}
                                color="success"
                              >
                                <AddCircle />
                              </IconButton>
                            </Tooltip>
                          )}
                          {shouldShowWarningIcon(link) && (
                            <Tooltip title={`Processing timeout: ${getCallbackStatusLabel(link)}`}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenReconcileModal(link)}
                                sx={{ color: '#ff9800' }}
                              >
                                <WarningAmber />
                              </IconButton>
                            </Tooltip>
                          )}
                          {shouldShowReconcileIcon(link) && (
                            <Tooltip title="Transaction declined/failed - reconcile required">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleOpenReconcileModal(link)}
                              >
                                <Build />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Collapsible Content Row */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          {hasChildren ? (
                            // Split Payment Breakdown
                            <Box sx={{ bgcolor: 'grey.50', p: 3, ml: 0 }}>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                                Split Payment Breakdown
                              </Typography>

                              <Grid container spacing={2}>
                                {children.map((child, index) => (
                                  <Grid item xs={12} md={6} lg={4} key={child.id}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                          <Typography variant="subtitle2" fontWeight={600}>
                                            Payer {index + 1}
                                          </Typography>
                                          <StatusChip status={child.status} size="small" />
                                        </Box>

                                        <Typography variant="body2" fontWeight={600} gutterBottom>
                                          {child.splitPayerName}
                                        </Typography>

                                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                          {child.splitPayerEmail}
                                        </Typography>

                                        <Divider sx={{ my: 1.5 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            Assigned:
                                          </Typography>
                                          <Typography variant="caption" fontWeight={600}>
                                            {formatCurrency(child.splitAssignedAmount || child.invoiceAmount, child.currency)}
                                          </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            Paid:
                                          </Typography>
                                          <Typography variant="caption" fontWeight={600} color="success.main">
                                            {formatCurrency(child.totalPaid, child.currency)}
                                          </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            Remaining:
                                          </Typography>
                                          <Typography variant="caption" fontWeight={600} color="warning.main">
                                            {formatCurrency(child.remainingAmount, child.currency)}
                                          </Typography>
                                        </Box>

                                        {child.payments && child.payments.length > 0 && (
                                          <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider', mb: 1 }}>
                                            <Typography variant="caption" color="text.secondary">
                                              {child.payments.length} payment(s) • Last: {formatDateTime(child.payments[child.payments.length - 1].timestamp.toDate())}
                                            </Typography>
                                          </Box>
                                        )}

                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Visibility />}
                                            onClick={() => onViewDetails(child)}
                                            fullWidth
                                          >
                                            View
                                          </Button>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                              navigator.clipboard.writeText(`${window.location.origin}/pay/${child.linkId}`);
                                              toast.success('Link copied!');
                                            }}
                                          >
                                            <ContentCopy fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>

                              {/* Parent Summary */}
                              <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  Total Summary
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">
                                      Total Invoice
                                    </Typography>
                                    <Typography variant="h6" fontWeight={600}>
                                      {formatCurrency(link.invoiceAmount, link.currency)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">
                                      Total Paid
                                    </Typography>
                                    <Typography variant="h6" fontWeight={600} color="success.main">
                                      {formatCurrency(link.totalPaid, link.currency)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">
                                      Remaining
                                    </Typography>
                                    <Typography variant="h6" fontWeight={600} color="warning.main">
                                      {formatCurrency(link.remainingAmount, link.currency)}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Box>
                          ) : hasPayments ? (
                            // Payment History
                            <PaymentHistorySubRow
                              payments={getVisiblePayments(link)}
                              currency={link.currency}
                              totalPaid={link.totalPaid}
                              invoiceAmount={link.invoiceAmount}
                              remainingAmount={link.remainingAmount}
                              onEditManualPayment={(payment) => handleOpenEditManualPayment(link, payment)}
                              onDeleteManualPayment={(payment) => {
                                handleRequestSoftDeleteManualPayment(link, payment);
                              }}
                              manualActionLoadingPaymentId={manualActionLoadingPaymentId}
                            />
                          ) : null}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredLinks.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Manual Payment Dialog */}
      <AddManualPaymentDialog
        open={manualPaymentDialogOpen}
        onClose={handleCloseManualPayment}
        paymentLink={selectedLinkForPayment}
        mode={manualPaymentDialogMode}
        paymentToEdit={selectedManualPayment}
        onSuccess={handleManualPaymentSuccess}
      />

      <Dialog
        open={deleteManualConfirmOpen}
        onClose={handleCloseManualDeleteConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Manual Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {pendingManualDelete
              ? `Delete manual payment ${formatCurrency(pendingManualDelete.payment.amount, pendingManualDelete.link.currency)}?`
              : 'Delete this manual payment?'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            This is a soft delete. Record will be kept for audit logs.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManualDeleteConfirm} disabled={!!manualActionLoadingPaymentId}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleConfirmSoftDeleteManualPayment()}
            disabled={!!manualActionLoadingPaymentId}
          >
            {manualActionLoadingPaymentId ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reconcile Modal */}
      <Dialog open={reconcileModalOpen} onClose={handleCloseReconcileModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedLinkForReconcile && !!getEffectiveReconcileBlockReason()
            ? 'Payment Issue Details'
            : 'Reconcile Payment'}
        </DialogTitle>
        <DialogContent>
          {selectedLinkForReconcile && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Link ID:</strong> {selectedLinkForReconcile.linkId}
              </Typography>
              <Typography variant="body2">
                <strong>Customer:</strong> {selectedLinkForReconcile.customerName}
              </Typography>
              <Typography variant="body2">
                <strong>Invoice Amount:</strong>{' '}
                {formatCurrency(selectedLinkForReconcile.invoiceAmount, selectedLinkForReconcile.currency)}
              </Typography>
              <Typography variant="body2">
                <strong>Remaining Amount:</strong>{' '}
                {formatCurrency(selectedLinkForReconcile.remainingAmount, selectedLinkForReconcile.currency)}
              </Typography>
              <Typography variant="body2" fontWeight={600} color="primary">
                <strong>Bank Attempt Amount:</strong>{' '}
                {reconciliationDetailsLoading
                  ? 'Loading...'
                  : reconciliationDetails?.attemptedAmount !== null &&
                      reconciliationDetails?.attemptedAmount !== undefined
                    ? formatCurrency(
                        reconciliationDetails.attemptedAmount,
                        reconciliationDetails.currency || selectedLinkForReconcile.currency
                      )
                    : '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Ziraat Order ID:</strong>{' '}
                {reconciliationDetails?.orderId || selectedLinkForReconcile.ziraatOrderId || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Last Status:</strong> {getCallbackStatusLabel(selectedLinkForReconcile)}
              </Typography>
              {reconciliationDetails?.attemptStatus && (
                <Typography variant="body2">
                  <strong>Attempt Status:</strong> {reconciliationDetails.attemptStatus}
                </Typography>
              )}

              {getEffectiveReconcileBlockReason() && (
                <Box sx={{ p: 1.5, bgcolor: 'error.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="error.dark" fontWeight={600}>
                    {getEffectiveReconcileBlockReason()}
                  </Typography>
                </Box>
              )}

              {!getEffectiveReconcileBlockReason() &&
                !reconciliationDetailsLoading &&
                reconciliationDetails?.canReconcile && (
                <>
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="warning.dark">
                      Confirm only after verifying in the bank panel that the amount above was successfully charged.
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    size="small"
                    label="Bank Transaction ID (optional)"
                    value={reconcileBankTransactionId}
                    onChange={(event) => setReconcileBankTransactionId(event.target.value)}
                    helperText="Enter the separate bank transaction reference when it is available in the bank panel."
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={bankVerified}
                        onChange={(e) => setBankVerified(e.target.checked)}
                      />
                    }
                    label={
                      reconciliationDetails.attemptedAmount !== null
                        ? `I verified the ${formatCurrency(
                            reconciliationDetails.attemptedAmount,
                            reconciliationDetails.currency || selectedLinkForReconcile.currency
                          )} transaction in the bank panel`
                        : 'I verified this transaction in the bank panel'
                    }
                  />
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReconcileModal} disabled={!!reconcilingId}>
            Close
          </Button>
          {selectedLinkForReconcile && !getEffectiveReconcileBlockReason() && (
            <Button
              variant="contained"
              color="warning"
              disabled={
                !bankVerified ||
                !!reconcilingId ||
                reconciliationDetailsLoading ||
                !reconciliationDetails?.canReconcile ||
                reconciliationDetails.attemptedAmount === null
              }
              onClick={() => void handleQuickReconcile()}
            >
              {reconcilingId ? 'Reconciling...' : 'Confirm Reconcile'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentLinksTable;
